import { type ThunkAction, type UnknownAction } from "@reduxjs/toolkit";

import type { RootState } from "@/app/store";
import { TokenStorageService } from "@/shared/services/tokenStorage.service";
import { readApiCode, readApiStatus } from "@/shared/utils/apiError";

import { registerService } from "../services/registerService";
import type {
  ChangeEmailErrorKind,
  ChangeVerificationEmailRequestDto,
  RegisterRequestDto,
  ResendVerificationRequestDto,
  VerifyEmailRequestDto,
} from "../types/register.types";
import { clearSignupAttribution } from "../utils/signupAttribution";
import { setUser } from "./authSlice";
import { thunkInitializeAuth } from "./authThunks";
import {
  clearError,
  clearResendError,
  clearVerifyError,
  setEmailConflict,
  setError,
  setLastResendAt,
  setLoading,
  setResendError,
  setResending,
  setSuccess,
  setVerified,
  setVerifyError,
  setVerifying,
  setChangingEmail,
  verificationEmailChanged,
} from "./registerSlice";

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (typeof error === "string") return error;
  if (typeof error === "object" && error !== null) {
    const maybeAxiosError = error as {
      response?: { status?: number; data?: { message?: string } };
      message?: string;
    };
    return maybeAxiosError.response?.data?.message ?? maybeAxiosError.message ?? fallback;
  }
  return fallback;
};

const getErrorStatus = (error: unknown): number | undefined => {
  if (typeof error === "object" && error !== null) {
    const maybeAxiosError = error as { response?: { status?: number } };
    return maybeAxiosError.response?.status;
  }
  return undefined;
};

// Single submit, atomic on backend. Frontend reports failure on any non-2xx;
// redirect is owned by the page via `success` flag.
export const thunkRegister =
  (
    body: RegisterRequestDto,
    turnstileToken?: string | null,
  ): ThunkAction<void, RootState, unknown, UnknownAction> =>
  async (dispatch) => {
    dispatch(clearError());
    dispatch(setLoading({ isLoading: true }));
    try {
      const data = await registerService.register(body, turnstileToken);

      if (data.access_token) {
        TokenStorageService.setToken(data.access_token);
      }

      if (data.refresh_token) {
        TokenStorageService.setRefreshToken(data.refresh_token);
      }

      if (data.user) {
        dispatch(setUser({ user: data.user }));
      }

      clearSignupAttribution();
      dispatch(setSuccess({ success: true }));
      dispatch(setError({ error: null }));
    } catch (error: unknown) {
      const status = getErrorStatus(error);
      if (status === 409) {
        dispatch(setEmailConflict({ emailConflict: true }));
      }
      dispatch(
        setError({
          error: getErrorMessage(error, "Error en el registro"),
        }),
      );
    } finally {
      dispatch(setLoading({ isLoading: false }));
    }
  };

// Posts email+code to /auth/verify-email. On success we re-hydrate the
// session via thunkInitializeAuth so `auth.user.email_verified_at` reflects
// the server timestamp — that flip is what opens the PrivateRoute gate.
export const thunkVerifyEmail =
  (body: VerifyEmailRequestDto): ThunkAction<Promise<void>, RootState, unknown, UnknownAction> =>
  async (dispatch) => {
    dispatch(clearVerifyError());
    dispatch(setVerifying({ isVerifying: true }));
    try {
      const data = await registerService.verifyEmail(body);
      if (data.verified) {
        dispatch(setVerified({ verified: true }));
        // Re-fetch /auth/me so email_verified_at comes from the server.
        await (dispatch(thunkInitializeAuth()) as unknown as Promise<void>);
      } else {
        dispatch(setVerifyError({ verifyError: "verify_error_generic" }));
      }
    } catch (error: unknown) {
      dispatch(
        setVerifyError({
          verifyError: getErrorMessage(error, "Error al verificar el código"),
        }),
      );
    } finally {
      dispatch(setVerifying({ isVerifying: false }));
    }
  };

// Posts email to /auth/resend-verification. Records lastResendAt for the UI
// cooldown. Backend rate-limit (3/min) surfaces as 429 → resendError.
export const thunkResendVerificationCode =
  (body: ResendVerificationRequestDto): ThunkAction<Promise<void>, RootState, unknown, UnknownAction> =>
  async (dispatch) => {
    dispatch(clearResendError());
    dispatch(setResending({ isResending: true }));
    try {
      await registerService.resendVerificationCode(body);
      dispatch(setLastResendAt({ lastResendAt: Date.now() }));
    } catch (error: unknown) {
      const status = getErrorStatus(error);
      const fallback = status === 429 ? "verify_error_rate_limited" : "Error reenviando el código";
      dispatch(setResendError({ resendError: getErrorMessage(error, fallback) }));
    } finally {
      dispatch(setResending({ isResending: false }));
    }
  };

// The backend tags each refusal of PATCH /auth/verification-email with a `code`; the status covers the rest.
const CHANGE_EMAIL_ERROR_BY_CODE: Record<string, ChangeEmailErrorKind> = {
  EMAIL_UNCHANGED: "unchanged",
  USER_EMAIL_IN_USE: "user_in_use",
  ORGANIZATION_EMAIL_IN_USE: "organization_in_use",
  EMAIL_ALREADY_VERIFIED: "already_verified",
};

const classifyChangeEmailError = (error: unknown): ChangeEmailErrorKind => {
  const code = readApiCode(error);
  if (code && CHANGE_EMAIL_ERROR_BY_CODE[code]) return CHANGE_EMAIL_ERROR_BY_CODE[code];
  const status = readApiStatus(error);
  if (status === 429) return "rate_limited";
  if (status === 409) return "user_in_use";
  // A 400 without a code is the ValidationPipe refusing the address itself.
  if (status === 400) return "invalid_email";
  return "generic";
};

// Corrects a mistyped address while it is still unverified: the backend moves the email (and the organization's,
// when it mirrored it) and mails a fresh code there. Resolves `null` on success, or why it was refused — the
// field that asked owns that sentence, so the refusal is handed back rather than parked in the slice.
export const thunkChangeVerificationEmail =
  (
    body: ChangeVerificationEmailRequestDto,
  ): ThunkAction<Promise<ChangeEmailErrorKind | null>, RootState, unknown, UnknownAction> =>
  async (dispatch) => {
    dispatch(setChangingEmail({ isChangingEmail: true }));
    try {
      const data = await registerService.changeVerificationEmail(body);
      dispatch(
        verificationEmailChanged({
          email: data.user.email,
          organizationEmailUpdated: data.organization_email_updated,
          sentAt: Date.now(),
        }),
      );
      return null;
    } catch (error: unknown) {
      return classifyChangeEmailError(error);
    } finally {
      dispatch(setChangingEmail({ isChangingEmail: false }));
    }
  };
